import { useEffect, useState, useRef } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { User, Lock, Mail, Shield, Eye, EyeOff, Save, Loader2, DollarSign, QrCode, Upload, Trash2, AlertTriangle, Clock, Bell, Skull } from "lucide-react";

export default function Profile() {
  const { t } = useT();
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changing, setChanging] = useState(false);

  // Payment settings
  const [pay, setPay] = useState({
    zelle_email: "",
    zelle_phone: "",
    zelle_account_name: "",
    pix_key: "",
    zelle_qr_file_id: null,
    signature_data_url: null,
    signature_owner_name: "",
    signature_owner_title: "",
  });
  const [savingPay, setSavingPay] = useState(false);
  const [uploadingQr, setUploadingQr] = useState(false);
  const [envFlags, setEnvFlags] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/auth/me");
      setMe(r.data);
      setName(r.data.name || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const loadPayment = async () => {
    try {
      const r = await api.get("/settings/payment");
      setPay({
        zelle_email: r.data.zelle_email || "",
        zelle_phone: r.data.zelle_phone || "",
        zelle_account_name: r.data.zelle_account_name || "",
        pix_key: r.data.pix_key || "",
        zelle_qr_file_id: r.data.zelle_qr_file_id || null,
        signature_data_url: r.data.signature_data_url || null,
        signature_owner_name: r.data.signature_owner_name || "",
        signature_owner_title: r.data.signature_owner_title || "",
      });
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    load();
    loadPayment();
    api.get("/settings/environment").then(r => setEnvFlags(r.data)).catch(() => {});
  }, []);

  const saveName = async () => {
    if (!name.trim() || name === me?.name) return;
    setSavingName(true);
    try {
      await api.put("/auth/profile", { name: name.trim() });
      toast.success(t("profile.profileUpdated"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingName(false);
    }
  };

  const savePayment = async () => {
    setSavingPay(true);
    try {
      await api.put("/settings/payment", {
        zelle_email: pay.zelle_email || null,
        zelle_phone: pay.zelle_phone || null,
        zelle_account_name: pay.zelle_account_name || null,
        pix_key: pay.pix_key || null,
        zelle_qr_file_id: pay.zelle_qr_file_id || null,
      });
      toast.success(t("profile.paymentSaved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingPay(false);
    }
  };

  const uploadQr = async (file) => {
    if (!file) return;
    setUploadingQr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", "settings");
      fd.append("owner_id", "company_payment_settings");
      fd.append("label", "zelle_qr");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newPay = { ...pay, zelle_qr_file_id: r.data.id };
      setPay(newPay);
      await api.put("/settings/payment", {
        zelle_email: newPay.zelle_email || null,
        zelle_phone: newPay.zelle_phone || null,
        zelle_account_name: newPay.zelle_account_name || null,
        pix_key: newPay.pix_key || null,
        zelle_qr_file_id: newPay.zelle_qr_file_id,
      });
      toast.success(t("profile.qrUploaded"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploadingQr(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeQr = async () => {
    if (!window.confirm(t("profile.confirmRemoveQr"))) return;
    setUploadingQr(true);
    try {
      const newPay = { ...pay, zelle_qr_file_id: null };
      setPay(newPay);
      await api.put("/settings/payment", {
        zelle_email: newPay.zelle_email || null,
        zelle_phone: newPay.zelle_phone || null,
        zelle_account_name: newPay.zelle_account_name || null,
        pix_key: newPay.pix_key || null,
        zelle_qr_file_id: null,
      });
      toast.success(t("profile.qrRemoved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploadingQr(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t("profile.passwordsDontMatch"));
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t("profile.passwordTooShort"));
      return;
    }
    setChanging(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(t("profile.passwordChanged"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setChanging(false);
    }
  };

  const strength = (() => {
    if (!newPassword) return null;
    let score = 0;
    if (newPassword.length >= 8) score++;
    if (/[A-Z]/.test(newPassword)) score++;
    if (/[0-9]/.test(newPassword)) score++;
    if (/[^A-Za-z0-9]/.test(newPassword)) score++;
    return score;
  })();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <div className="overline">{t("profile.pageTitle")}</div>
        <p className="text-sm text-zinc-500 mt-1">{t("profile.pageSubtitle")}</p>
      </div>

      {envFlags && (envFlags.stripe_sandbox || envFlags.twilio_sandbox) && (
        <div className="border-l-4 border-amber-500 bg-amber-50 p-4" data-testid="sandbox-warning-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <div className="font-display font-bold text-amber-900 tracking-tight">
                {t("profile.sandboxWarningTitle")}
              </div>
              <ul className="text-xs text-amber-900 space-y-1.5 list-disc pl-4">
                {envFlags.stripe_sandbox && (
                  <li data-testid="stripe-sandbox-warning">
                    <strong>{t("profile.stripeSandboxLabel")}</strong> · {t("profile.stripeSandboxMsg")}
                  </li>
                )}
                {envFlags.twilio_sandbox && (
                  <li data-testid="twilio-sandbox-warning">
                    <strong>{t("profile.twilioSandboxLabel")}</strong> · {t("profile.twilioSandboxMsg", { from: envFlags.twilio_from_label })}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Account info card */}
      <div className="border border-zinc-200 bg-white" data-testid="account-info-card">
        <div className="border-b border-zinc-200 px-4 py-3 flex items-center gap-2">
          <User className="w-4 h-4 text-[#F5A623]" />
          <div className="font-display font-semibold tracking-tight">{t("profile.accountInfo")}</div>
        </div>
        <div className="p-4 space-y-4">
          <Row icon={<Mail className="w-3.5 h-3.5" />} label={t("profile.email")} value={me?.email} />
          <Row icon={<Shield className="w-3.5 h-3.5" />} label={t("profile.role")} value={me?.role?.toUpperCase()} />
          <div className="space-y-1.5">
            <label className="overline">{t("profile.name")}</label>
            <div className="flex gap-2">
              <Input
                className="rounded-none"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="profile-name-input"
              />
              <Button
                onClick={saveName}
                disabled={savingName || !name.trim() || name === me?.name}
                className="rounded-none bg-[#F5A623] hover:bg-[#D97706]"
                data-testid="save-name-button"
              >
                {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment settings card */}
      <div className="border border-zinc-200 bg-white" data-testid="payment-settings-card">
        <div className="border-b border-zinc-200 px-4 py-3 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-[#F5A623]" />
          <div className="font-display font-semibold tracking-tight">{t("profile.paymentTitle")}</div>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-xs text-zinc-500 leading-relaxed">
            {t("profile.paymentSubtitle")}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field
              label={t("profile.zelleEmail")}
              value={pay.zelle_email}
              onChange={(v) => setPay({ ...pay, zelle_email: v })}
              placeholder="pagamentos@movix.com"
              testId="zelle-email-input"
              type="email"
            />
            <Field
              label={t("profile.zellePhone")}
              value={pay.zelle_phone}
              onChange={(v) => setPay({ ...pay, zelle_phone: v })}
              placeholder="+1 561 455 6277"
              testId="zelle-phone-input"
            />
            <Field
              label={t("profile.zelleAccountName")}
              value={pay.zelle_account_name}
              onChange={(v) => setPay({ ...pay, zelle_account_name: v })}
              placeholder="Movix Car Rental LLC"
              testId="zelle-name-input"
              className="md:col-span-2"
            />
            <Field
              label={t("profile.pixKey")}
              value={pay.pix_key}
              onChange={(v) => setPay({ ...pay, pix_key: v })}
              placeholder="pix@movix.com"
              testId="pix-key-input"
              className="md:col-span-2"
            />
          </div>

          {/* QR Code Zelle */}
          <div className="border-t border-zinc-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <QrCode className="w-4 h-4 text-zinc-600" />
              <div className="overline">{t("profile.zelleQrTitle")}</div>
            </div>
            <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
              {t("profile.zelleQrHint")}
            </p>

            {pay.zelle_qr_file_id ? (
              <div className="flex items-start gap-4">
                <img
                  src={`${API}/files/${pay.zelle_qr_file_id}/raw`}
                  alt="Zelle QR"
                  className="w-40 h-40 border border-zinc-200 object-contain bg-white"
                  data-testid="zelle-qr-preview"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={removeQr}
                  disabled={uploadingQr}
                  className="rounded-none border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="remove-zelle-qr"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> {t("profile.removeQr")}
                </Button>
              </div>
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => uploadQr(e.target.files?.[0])}
                  data-testid="zelle-qr-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingQr}
                  className="rounded-none border-zinc-400 hover:border-[#F5A623]"
                  data-testid="upload-zelle-qr"
                >
                  {uploadingQr ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {t("profile.uploadQr")}
                </Button>
              </div>
            )}
          </div>

          <Button
            onClick={savePayment}
            disabled={savingPay}
            className="rounded-none bg-black hover:bg-zinc-800 text-white"
            data-testid="save-payment-settings"
          >
            {savingPay ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {t("profile.savePayment")}
          </Button>
        </div>
      </div>

      <RemindersCard />

      <OwnerSignatureCard
        initialSignature={pay.signature_data_url}
        initialName={pay.signature_owner_name}
        initialTitle={pay.signature_owner_title}
        currentPay={pay}
        onSaved={(signature_data_url, signature_owner_name, signature_owner_title) =>
          setPay((prev) => ({ ...prev, signature_data_url, signature_owner_name, signature_owner_title }))
        }
      />

      {/* Change password card */}
      <form onSubmit={changePassword} className="border border-zinc-200 bg-white" data-testid="change-password-card">
        <div className="border-b border-zinc-200 px-4 py-3 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#F5A623]" />
          <div className="font-display font-semibold tracking-tight">{t("profile.changePassword")}</div>
        </div>
        <div className="p-4 space-y-4">
          <PasswordField
            label={t("profile.currentPassword")}
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            onToggle={() => setShowCurrent(!showCurrent)}
            testId="current-password-input"
          />
          <PasswordField
            label={t("profile.newPassword")}
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            onToggle={() => setShowNew(!showNew)}
            testId="new-password-input"
          />
          {strength !== null && (
            <div className="space-y-1" data-testid="password-strength">
              <div className="flex gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 transition-colors ${
                      i < strength
                        ? strength <= 1 ? "bg-red-500"
                        : strength === 2 ? "bg-amber-500"
                        : strength === 3 ? "bg-green-500"
                        : "bg-emerald-600"
                        : "bg-zinc-200"
                    }`}
                  />
                ))}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase">
                {t(`profile.strength.${["weak","weak","fair","good","strong"][strength]}`)}
              </div>
            </div>
          )}
          <PasswordField
            label={t("profile.confirmPassword")}
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showNew}
            onToggle={() => setShowNew(!showNew)}
            testId="confirm-password-input"
            error={confirmPassword && newPassword !== confirmPassword ? t("profile.passwordsDontMatch") : null}
          />

          <div className="bg-orange-50 border border-orange-200 px-3 py-2 text-[11px] text-zinc-700">
            {t("profile.passwordTips")}
          </div>

          <Button
            type="submit"
            disabled={changing || !currentPassword || !newPassword || newPassword !== confirmPassword}
            className="rounded-none bg-black hover:bg-zinc-800 text-white w-full"
            data-testid="change-password-button"
          >
            {changing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            {changing ? t("common.loading") : t("profile.updatePassword")}
          </Button>
        </div>
      </form>

      {me?.role === "admin" && <DangerZone />}
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-100 last:border-0 py-1.5">
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        {icon} <span className="overline">{label}</span>
      </div>
      <div className="font-mono text-sm">{value || "—"}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId, type = "text", className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="overline">{label}</label>
      <Input
        type={type}
        className="rounded-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
      />
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, testId, error }) {
  return (
    <div className="space-y-1.5">
      <label className="overline">{label}</label>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          className="rounded-none pr-10"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && <div className="text-[11px] text-red-600 font-mono">{error}</div>}
    </div>
  );
}

function RemindersCard() {
  const { t } = useT();
  const [running, setRunning] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);
  const [logs, setLogs] = useState([]);

  const loadLogs = async () => {
    try {
      const r = await api.get("/reminders/log?limit=10");
      setLogs(r.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadLogs(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reminders/run-now");
      setLastSummary(r.data);
      toast.success(t("profile.remindersDispatched", { n: r.data.sent }));
      loadLogs();
    } catch (e) {
      toast.error(t("profile.remindersFailed"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="reminders-card">
      <div className="border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#F5A623]" />
          <div className="font-display font-semibold tracking-tight">{t("profile.remindersTitle")}</div>
        </div>
        <Button
          onClick={runNow}
          disabled={running}
          variant="outline"
          className="rounded-none border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623] hover:text-white"
          data-testid="run-reminders-now"
        >
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
          {t("profile.runRemindersNow")}
        </Button>
      </div>
      <div className="p-4 space-y-3">
        <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 font-mono leading-relaxed">
          {t("profile.remindersHint")}
        </div>
        {lastSummary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <Stat label={t("profile.statSent")} value={lastSummary.sent} color="emerald" />
            <Stat label={t("profile.statDedup")} value={lastSummary.skipped_already_sent_today} color="zinc" />
            <Stat label={t("profile.statNoPhone")} value={lastSummary.skipped_no_phone} color="zinc" />
            <Stat label={t("profile.statErrors")} value={lastSummary.twilio_errors} color="rose" />
          </div>
        )}
        {logs.length > 0 && (
          <div>
            <div className="overline mb-1.5">{t("profile.recentReminders")}</div>
            <div className="max-h-48 overflow-y-auto border border-zinc-100 divide-y divide-zinc-100">
              {logs.map((lg, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-1.5 text-[11px] font-mono">
                  <span className={lg.sent ? "text-emerald-700" : "text-rose-700"}>
                    {lg.sent ? "✓" : "✗"} {lg.state.toUpperCase()}
                  </span>
                  <span className="truncate flex-1 mx-2 text-zinc-600">{lg.phone}</span>
                  <span className="tabular-nums">${(lg.balance_due || 0).toFixed(2)}</span>
                  <span className="text-zinc-400 ml-2">{lg.created_at?.slice(0, 16)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  const cls = {
    emerald: "border-emerald-300 text-emerald-700",
    zinc: "border-zinc-300 text-zinc-700",
    rose: "border-rose-300 text-rose-700",
  }[color] || "border-zinc-300 text-zinc-700";
  return (
    <div className={`border ${cls} p-2`}>
      <div className="font-display font-bold text-xl">{value}</div>
      <div className="text-[9px] font-mono uppercase tracking-wider opacity-70">{label}</div>
    </div>
  );
}


function OwnerSignatureCard({ initialSignature, initialName, initialTitle, currentPay, onSaved }) {
  const { t } = useT();
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(!initialSignature);
  const [name, setName] = useState(initialName || "");
  const [title, setTitle] = useState(initialTitle || "");
  const [saving, setSaving] = useState(false);
  const [drawingMode, setDrawingMode] = useState(!initialSignature);

  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";
    if (initialSignature && !drawingMode) {
      const img = new window.Image();
      img.onload = () => { ctx.drawImage(img, 0, 0, rect.width, rect.height); setIsEmpty(false); };
      img.src = initialSignature;
    }
  };

  useEffect(() => { if (drawingMode) resize(); }, [drawingMode]); // eslint-disable-line

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const tt = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: tt.clientX - rect.left, y: tt.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawingRef.current = true; lastRef.current = pos(e); setIsEmpty(false); };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };
  const end = () => { drawingRef.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error(t("ownerSignature.nameRequired"));
      return;
    }
    if (drawingMode && isEmpty) {
      toast.error(t("ownerSignature.signatureRequired"));
      return;
    }
    setSaving(true);
    try {
      const dataUrl = drawingMode ? canvasRef.current.toDataURL("image/png") : initialSignature;
      await api.put("/settings/payment", {
        ...currentPay,
        signature_data_url: dataUrl,
        signature_owner_name: name.trim(),
        signature_owner_title: title.trim() || null,
      });
      toast.success(t("ownerSignature.saved"));
      setDrawingMode(false);
      onSaved?.(dataUrl, name.trim(), title.trim() || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeSig = async () => {
    if (!window.confirm(t("ownerSignature.confirmRemove"))) return;
    try {
      await api.put("/settings/payment", {
        ...currentPay,
        signature_data_url: null,
        signature_owner_name: null,
        signature_owner_title: null,
      });
      toast.success(t("ownerSignature.removed"));
      setName(""); setTitle(""); setDrawingMode(true);
      onSaved?.(null, "", "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="owner-signature-card">
      <div className="border-b border-zinc-200 px-4 py-3 flex items-center gap-2">
        <Save className="w-4 h-4 text-[#F5A623]" />
        <div className="font-display font-semibold tracking-tight flex-1">{t("ownerSignature.title")}</div>
        {initialSignature && !drawingMode && (
          <button onClick={() => { setDrawingMode(true); setIsEmpty(true); setTimeout(resize, 50); }}
            className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-[#F5A623]"
            data-testid="redraw-signature-button">
            {t("ownerSignature.redraw")}
          </button>
        )}
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-zinc-600">{t("ownerSignature.description")}</p>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="overline">{t("ownerSignature.ownerName")}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none"
              placeholder="Ex: Alle Cesar" data-testid="signature-name-input" />
          </div>
          <div className="space-y-1.5">
            <label className="overline">{t("ownerSignature.ownerTitle")}</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none"
              placeholder={t("ownerSignature.titlePlaceholder")} data-testid="signature-title-input" />
          </div>
        </div>

        {drawingMode ? (
          <>
            <div className="relative border border-dashed border-zinc-300 bg-[radial-gradient(circle,#E4E4E7_1px,transparent_1px)] [background-size:16px_16px]">
              <canvas
                ref={canvasRef}
                className="w-full h-40 touch-none cursor-crosshair block"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                data-testid="signature-canvas"
              />
              {isEmpty && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("ownerSignature.drawHere")}</div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" onClick={clear} variant="outline" className="rounded-none" disabled={isEmpty || saving}
                data-testid="signature-clear-button">
                {t("ownerSignature.clear")}
              </Button>
              <Button onClick={save} className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-black font-semibold"
                disabled={saving || isEmpty || !name.trim()} data-testid="signature-save-button">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t("ownerSignature.save")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="border border-zinc-200 bg-zinc-50 p-4 flex items-center justify-center">
              <img src={initialSignature} alt="Owner signature" className="max-h-24" data-testid="signature-preview" />
            </div>
            <div className="text-center border-t border-zinc-200 pt-3">
              <div className="font-semibold">{name}</div>
              <div className="text-xs text-zinc-500">{title || t("ownerSignature.defaultTitle")} · Movix Car Rental LLC</div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button onClick={removeSig} variant="outline" className="rounded-none text-red-600 border-red-300 hover:bg-red-50"
                data-testid="signature-remove-button">
                <Trash2 className="w-4 h-4 mr-2" /> {t("ownerSignature.remove")}
              </Button>
              <Button onClick={save} className="rounded-none bg-black hover:bg-zinc-800 text-white"
                disabled={saving || !name.trim()} data-testid="signature-update-button">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t("ownerSignature.updateNames")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function DangerZone() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [wiping, setWiping] = useState(false);

  const wipe = async () => {
    if (confirm !== "ZERAR TUDO") {
      toast.error(t("dangerZone.wrongPhrase"));
      return;
    }
    setWiping(true);
    try {
      const r = await api.post("/admin/wipe-operational-data", { confirmation: confirm });
      toast.success(t("dangerZone.success", { n: r.data.total_deleted }));
      setOpen(false);
      setConfirm("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="border-2 border-red-300 bg-red-50/40" data-testid="danger-zone">
      <div className="border-b-2 border-red-300 px-4 py-3 flex items-center gap-2 bg-red-100/60">
        <Skull className="w-4 h-4 text-red-600" />
        <div className="font-display font-semibold tracking-tight text-red-900">{t("dangerZone.title")}</div>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-zinc-700">{t("dangerZone.description")}</p>
        <ul className="text-xs text-zinc-600 list-disc list-inside space-y-0.5">
          <li>{t("dangerZone.wipeList1")}</li>
          <li>{t("dangerZone.wipeList2")}</li>
          <li>{t("dangerZone.wipeList3")}</li>
        </ul>
        <p className="text-xs font-mono text-emerald-700">{t("dangerZone.preservedNote")}</p>

        {!open ? (
          <Button
            onClick={() => setOpen(true)}
            className="rounded-none bg-red-600 hover:bg-red-700 text-white"
            data-testid="wipe-data-button"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t("dangerZone.wipeButton")}
          </Button>
        ) : (
          <div className="border border-red-400 bg-white p-3 space-y-3" data-testid="wipe-confirmation">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="text-xs text-zinc-700">
                <strong>{t("dangerZone.confirmTitle")}</strong>
                <br />
                {t("dangerZone.typePhrase")} <code className="bg-zinc-100 px-1 font-mono font-bold">ZERAR TUDO</code>
              </div>
            </div>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="ZERAR TUDO"
              className="rounded-none font-mono uppercase"
              data-testid="wipe-confirm-input"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setOpen(false); setConfirm(""); }}
                disabled={wiping}
                className="rounded-none flex-1"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={wipe}
                disabled={wiping || confirm !== "ZERAR TUDO"}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white flex-1"
                data-testid="wipe-confirm-button"
              >
                {wiping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {t("dangerZone.confirmWipe")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
